import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { API_BASE_URL } from '../config/api';

interface CashLoopConfig {
  devices: string[];
  integrations: string[];
  environments: string[];
  maxDevices: number;
  integrationConcurrency: number;
  continueOnFail: boolean;
  retries: number;
  runSQLValidation: boolean;
  intervalMs?: number;
  cashOnlyKeepLogin?: boolean;
  cashOnlyRestartEvery?: number;
}

interface CashLoopStatus {
  active: boolean;
  config?: CashLoopConfig;
  failureCount: number;
  lastFailureAt?: string;
  lastAlertAt?: string;
  lastError?: {
    errorType?: string;
    errorMessage?: string;
    step?: string;
    timestamp?: string;
    category?: string;
  };
}

interface DeviceItem {
  id: string;
  name?: string;
  status?: string;
}

interface DevicePoolResponse {
  devices: DeviceItem[];
  selectedDevices: string[];
  maxDevices: number;
}

const DEFAULT_CONFIG: CashLoopConfig = {
  devices: [],
  integrations: ['square'],
  environments: ['production-new'],
  maxDevices: 2,
  integrationConcurrency: 1,
  continueOnFail: false,
  retries: 0,
  runSQLValidation: true,
  intervalMs: 10 * 60 * 1000,
  cashOnlyKeepLogin: true,
  cashOnlyRestartEvery: 1
};

export default function CashOnlyControl() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<CashLoopStatus | null>(null);
  const [devices, setDevices] = useState<DeviceItem[]>([]);
  const [config, setConfig] = useState<CashLoopConfig>(DEFAULT_CONFIG);
  const [isDirty, setIsDirty] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info'
  });

  const intervalMinutes = useMemo(() => {
    const ms = typeof config.intervalMs === 'number'
      ? config.intervalMs
      : DEFAULT_CONFIG.intervalMs ?? 0;
    return ms / 60000;
  }, [config.intervalMs]);

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/queue/cash-loop/status`);
      const data = await response.json();
      if (data.success) {
        setStatus(data.data);
        if (data.data?.config) {
          const shouldSync = !isDirty || !!data.data?.active;
          if (shouldSync) {
            setConfig(prev => ({ ...prev, ...data.data.config }));
            setIsDirty(false);
          }
        }
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to fetch status', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchDevicePool = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/queue/cash-loop/device-pool`);
      const data = await response.json();
      if (data?.success && data?.data) {
        const payload: DevicePoolResponse = data.data;
        setDevices(payload.devices || []);
        if (payload.selectedDevices?.length && !config.devices.length) {
          setConfig(prev => ({ ...prev, devices: payload.selectedDevices || [] }));
        }
        if (typeof payload.maxDevices === 'number' && payload.maxDevices > 0) {
          setConfig(prev => ({ ...prev, maxDevices: payload.maxDevices }));
        }
      }
    } catch (error) {
      // ignore device list errors
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchDevicePool();
    const interval = setInterval(fetchStatus, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleStart = async () => {
    try {
      const minRequired = 1;
      if (config.devices.length < minRequired || config.devices.length > 3) {
        setSnackbar({ open: true, message: 'Please select 1–3 devices', severity: 'error' });
        return;
      }
      const payload = {
        ...config,
        intervalMs: typeof config.intervalMs === 'number'
          ? config.intervalMs
          : DEFAULT_CONFIG.intervalMs
      };
      const response = await fetch(`${API_BASE_URL}/api/queue/cash-loop/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        setSnackbar({ open: true, message: 'Cash-only loop started', severity: 'success' });
        setIsDirty(false);
        fetchStatus();
      } else {
        setSnackbar({ open: true, message: data.error || 'Start failed', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Start failed', severity: 'error' });
    }
  };

  const handleSave = async () => {
    try {
      const minRequired = 1;
      if (config.devices.length < minRequired || config.devices.length > 3) {
        setSnackbar({ open: true, message: 'Please select 1–3 devices', severity: 'error' });
        return;
      }
      const payload = {
        ...config,
        intervalMs: typeof config.intervalMs === 'number'
          ? config.intervalMs
          : DEFAULT_CONFIG.intervalMs
      };
      const response = await fetch(`${API_BASE_URL}/api/queue/cash-loop/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        setSnackbar({ open: true, message: 'Cash-only config saved', severity: 'success' });
        if (data.data) {
          setConfig(prev => ({ ...prev, ...data.data }));
        }
        setIsDirty(false);
      } else {
        setSnackbar({ open: true, message: data.error || 'Save failed', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Save failed', severity: 'error' });
    }
  };

  const handleStop = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/queue/cash-loop/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stopRunning: true })
      });
      const data = await response.json();
      if (data.success) {
        setSnackbar({ open: true, message: 'Cash-only loop stopped', severity: 'success' });
        fetchStatus();
      } else {
        setSnackbar({ open: true, message: data.error || 'Stop failed', severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Stop failed', severity: 'error' });
    }
  };

  const handleConfigChange = <K extends keyof CashLoopConfig>(key: K, value: CashLoopConfig[K]) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h4" gutterBottom>
        Cash-Only Loop Control
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6">Status</Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={status?.active ? 'Active' : 'Stopped'}
                  color={status?.active ? 'success' : 'default'}
                />
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">Failure Count: {status?.failureCount ?? 0}</Typography>
                <Typography variant="body2">Last Failure: {status?.lastFailureAt || '-'}</Typography>
                <Typography variant="body2">Last Alert: {status?.lastAlertAt || '-'}</Typography>
                <Typography variant="body2">Last Error Type: {status?.lastError?.errorType || '-'}</Typography>
                <Typography variant="body2">Last Error Step: {status?.lastError?.step || '-'}</Typography>
                <Typography variant="body2">Last Error Time: {status?.lastError?.timestamp || '-'}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button variant="outlined" onClick={handleSave} disabled={!isDirty}>
                Save
              </Button>
              <Button variant="contained" color="success" onClick={handleStart} disabled={!!status?.active}>
                Start
              </Button>
              <Button variant="outlined" color="error" onClick={handleStop} disabled={!status?.active}>
                Stop
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Configuration
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Recommended: Square-only, 2–3 devices, keep login, low retries.
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Devices</InputLabel>
                <Select
                  multiple
                  value={config.devices}
                  onChange={(e) => handleConfigChange('devices', e.target.value as string[])}
                  renderValue={(selected) => selected.join(', ')}
                  label="Devices"
                >
                  {devices.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      <Checkbox checked={config.devices.includes(d.id)} />
                      <ListItemText primary={d.name || d.id} secondary={d.status} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Integrations</InputLabel>
                <Select
                  multiple
                  value={config.integrations}
                  onChange={(e) => handleConfigChange('integrations', e.target.value as string[])}
                  renderValue={(selected) => selected.join(', ')}
                  label="Integrations"
                  disabled
                >
                  {['square'].map((i) => (
                    <MenuItem key={i} value={i}>
                      <Checkbox checked={config.integrations.includes(i)} />
                      <ListItemText primary={i} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Environments</InputLabel>
                <Select
                  multiple
                  value={config.environments}
                  onChange={(e) => handleConfigChange('environments', e.target.value as string[])}
                  renderValue={(selected) => selected.join(', ')}
                  label="Environments"
                >
                  {['production', 'beta', 'production-new'].map((env) => (
                    <MenuItem key={env} value={env}>
                      <Checkbox checked={config.environments.includes(env)} />
                      <ListItemText primary={env} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Max Devices"
                type="number"
                value={config.maxDevices}
                onChange={(e) => handleConfigChange('maxDevices', Number(e.target.value || 0))}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Integration Concurrency"
                type="number"
                value={config.integrationConcurrency}
                onChange={(e) => handleConfigChange('integrationConcurrency', Number(e.target.value || 0))}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Retries"
                type="number"
                value={config.retries}
                onChange={(e) => handleConfigChange('retries', Number(e.target.value || 0))}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Interval (ms)"
                type="number"
                value={config.intervalMs ?? ''}
                onChange={(e) => handleConfigChange('intervalMs', Number(e.target.value || 0))}
                helperText={`~${intervalMinutes.toFixed(2)} minutes (0 = run immediately)`}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.continueOnFail}
                    onChange={(e) => handleConfigChange('continueOnFail', e.target.checked)}
                  />
                }
                label="Continue On Fail"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.runSQLValidation}
                    onChange={(e) => handleConfigChange('runSQLValidation', e.target.checked)}
                  />
                }
                label="Run SQL Validation"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!config.cashOnlyKeepLogin}
                    onChange={(e) => handleConfigChange('cashOnlyKeepLogin', e.target.checked)}
                  />
                }
                label="Keep Login (Cash-Only)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Restart Every N Runs"
                type="number"
                value={config.cashOnlyRestartEvery ?? 1}
                onChange={(e) => handleConfigChange('cashOnlyRestartEvery', Number(e.target.value || 1))}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider sx={{ my: 3 }} />

      <Alert severity="warning">
        Cash-only loop uses the shared queue. Avoid triggering normal runs at the same time.
      </Alert>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
